import React, { useState, useEffect, useRef } from "react";
import { Button as MaterialButton, FormControl, FormLabel, RadioGroup, Radio, FormControlLabel, TextField } from "@mui/material";
import { useNode, useEditor } from "@craftjs/core";
import { useTextPersistence } from "qwanyx";
import { useStack } from 'qwanyx'; // Import useStack hook

export const Button = ({ 
  name, 
  size, 
  variant, 
  color, 
  children, 
  hidden, 
  style, 
  qwantumId,
  script = "",
  targetCardId = "", // New prop for target card ID
  onSave,
  ...props 
}) => {
  const { 
    connectors: { connect, drag },
    isSelected,
    isHovered,
    hasSelectedNode,
    actions: { setProp }
  } = useNode((state) => ({
    isSelected: state.events.selected,
    isHovered: state.events.hovered,
    hasSelectedNode: state.events.selected
  }));

  const { enabled } = useEditor((state) => ({
    enabled: state.options.enabled
  }));

  // Try to get stack context (might be null if not in a stack)
  const useStackSafely = () => {
    try {
      return useStack();
    } catch (error) {
      return null;
    }
  };

  const stackContext = useStackSafely();

  const { text, handleBlur: persistenceBlur } = useTextPersistence(qwantumId, onSave);
  const [editable, setEditable] = useState(false);
  const textRef = useRef(null);
  const cursorPos = useRef(0);

  useEffect(() => {
    !hasSelectedNode && setEditable(false);
  }, [hasSelectedNode]);

  if (hidden) return null;

  const saveCursorPosition = () => {
    const selection = window.getSelection();
    if (selection.rangeCount > 0) {
      const range = selection.getRangeAt(0);
      cursorPos.current = range.startOffset;
    }
  };

  const restoreCursorPosition = () => {
    if (textRef.current && editable) {
      const selection = window.getSelection();
      const range = document.createRange();
      const textNode = textRef.current.firstChild;
      
      if (textNode) {
        const pos = Math.min(cursorPos.current, textNode.textContent.length);
        range.setStart(textNode, pos);
        range.setEnd(textNode, pos);
        selection.removeAllRanges();
        selection.addRange(range);
      }
    }
  };

  const handleInput = (e) => {
    saveCursorPosition();
    setProp(props => props.children = e.target.textContent);
    setTimeout(restoreCursorPosition, 0);
  };

  const handleClick = (e) => {
    if (enabled) {
      setEditable(true);
    } else {
      // In preview mode
      
      // If we have a target card ID and stack context, navigate to that card
      if (targetCardId && stackContext) {
        try {
          stackContext.selectItem(targetCardId, true); // Select and show in detail view
          console.log(`Button navigated to card: ${targetCardId}`);
        } catch (error) {
          console.error("Error navigating to card:", error);
        }
      }

      // Also persist the script value if configured
      if (qwantumId && script) {
        // Create a synthetic event for persistence
        const syntheticEvent = {
          target: { value: script }
        };
        persistenceBlur(syntheticEvent);
        if (onSave) {
          onSave(script);
        }
      }
    }
  };

  // Selection styling
  const getSelectionStyle = () => {
    if (isSelected && !editable) {
      return {
        outline: '1px dashed #2196f3',
        outlineOffset: '2px'
      };
    }
    if (isHovered && !editable) {
      return {
        outline: '1px dashed #ff9800',
        outlineOffset: '1px'
      };
    }
    return {};
  };
  
  return (
    <MaterialButton 
      ref={ref => connect(drag(ref))} 
      name={name}
      size={size} 
      variant={variant} 
      color={color}
      onClick={handleClick}
      style={{
        transition: 'all 0.2s ease',
        position: 'relative',
        ...style,
        ...getSelectionStyle()
      }}
      {...props}
    >
      <span
        ref={textRef}
        contentEditable={editable && enabled}
        suppressContentEditableWarning={true}
        onInput={handleInput}
        onBlur={() => setEditable(false)}
        style={{
          outline: editable ? '1px dashed #ccc' : 'none',
          display: 'inline-block',
          minWidth: '1px'
        }}
      >
        {children}
      </span>
    </MaterialButton>
  );
};

const ButtonSettings = () => {
  const { actions: { setProp }, props } = useNode((node) => ({
    props: node.data.props
  }));

  return (
    <div>
      <FormControl fullWidth margin="normal">
        <FormLabel>Component Name</FormLabel>
        <TextField
          value={props.name || ''}
          onChange={(e) => setProp(props => props.name = e.target.value)}
          size="small"
          placeholder="Enter component name"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Button Text</FormLabel>
        <TextField
          value={props.children || ''}
          onChange={(e) => setProp(props => props.children = e.target.value)}
          size="small"
          placeholder="Enter button text"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Target Card ID</FormLabel>
        <TextField
          value={props.targetCardId || ''}
          onChange={(e) => setProp(props => props.targetCardId = e.target.value)}
          size="small"
          placeholder="Paste card _id to navigate to"
          helperText="When clicked, button will show this card in detail view"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Quantum ID</FormLabel>
        <TextField
          value={props.qwantumId || ''}
          onChange={(e) => setProp(props => props.qwantumId = e.target.value)}
          size="small"
          placeholder="Enter quantum ID for persistence"
        />
      </FormControl>

      <FormControl fullWidth margin="normal">
        <FormLabel>Script (Value to Persist)</FormLabel>
        <TextField
          value={props.script || ''}
          onChange={(e) => setProp(props => props.script = e.target.value)}
          size="small"
          multiline
          rows={3}
          placeholder="Enter value to persist on click"
        />
      </FormControl>

      <FormControl size="small" component="fieldset">
        <FormLabel component="legend">Size</FormLabel>
        <RadioGroup 
          defaultValue={props.size} 
          onChange={(e) => setProp(props => props.size = e.target.value)}
        >
          <FormControlLabel label="Small" value="small" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Medium" value="medium" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Large" value="large" control={<Radio size="small" color="primary" />} />
        </RadioGroup>
      </FormControl>
      
      <FormControl component="fieldset">
        <FormLabel component="legend">Variant</FormLabel>
        <RadioGroup 
          defaultValue={props.variant} 
          onChange={(e) => setProp(props => props.variant = e.target.value)}
        >
          <FormControlLabel label="Text" value="text" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Outlined" value="outlined" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Contained" value="contained" control={<Radio size="small" color="primary" />} />
        </RadioGroup>
      </FormControl>
      
      <FormControl component="fieldset">
        <FormLabel component="legend">Color</FormLabel>
        <RadioGroup 
          defaultValue={props.color} 
          onChange={(e) => setProp(props => props.color = e.target.value)}
        >
          <FormControlLabel label="Default" value="default" control={<Radio size="small" color="default" />} />
          <FormControlLabel label="Primary" value="primary" control={<Radio size="small" color="primary" />} />
          <FormControlLabel label="Secondary" value="secondary" control={<Radio size="small" color="primary" />} />
        </RadioGroup>
      </FormControl>
    </div>
  );
};

Button.craft = {
  props: { 
    name: "",
    size: "small", 
    variant: "contained",
    color: "primary",
    children: "Click me",
    qwantumId: "",
    script: "",
    targetCardId: "" // New default prop
  },
  related: { 
    settings: ButtonSettings
  }
};